import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import host from "../../global";
import { useSelector } from "react-redux";
import { Check, ArrowLeft } from "lucide-react";
import { loadRazorpayScript } from "../../utils/loadRazorpay";
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import SubscriptionUsageBox, { BOOL_DEFS } from "../orgadmin/SubscriptionUsageBox";
import { EVENTS, track } from "../../utils/analytics";
import usePurchaseOptions from "../../hooks/usePurchaseOptions";
import getCountryHint from "../../utils/countryHint";
import PurchaseConfirmModal from "../billing/PurchaseConfirmModal";
import { formatDate, formatCount, formatMoney } from "../billing/billingFormat";

const TOPUP_PLAN_ID = "topup_interview_pack";

// Billing cycles the page can offer. Which of them are actually shown depends on
// what /get_plans returns -- a cycle with no plan behind it would empty the page.
const CYCLE_OPTIONS = [
  { value: "monthly",   label: "Monthly"   },
  { value: "quarterly", label: "Quarterly" },
  { value: "annual",    label: "Annual"    },
];

// Prices are shown as a monthly equivalent, with the amount actually billed
// underneath. Rounded up so the monthly figure never undersells the bill.
const CYCLE_MONTHS = { monthly: 1, quarterly: 3, annual: 12 };
const CYCLE_ADVERB = { monthly: "monthly", quarterly: "quarterly", annual: "yearly" };
const CYCLE_NOUN = { monthly: "month", quarterly: "quarter", annual: "year" };

// Highlights that restate a card's headline numbers -- "50 AI Interviews",
// "2,000 CV Screenings", "150+ AI Interviews a quarter", "Custom CV Screening
// volume". Seat lines ("Up to 5 Users") are kept: seats are one of the
// differences a card lists.
const RESTATES_HEADLINE = /^(\d[\d,]*\+?|custom)\s+(ai interviews?|cv screenings?)\b/i;

// A card lists only what changes from the plan to its left, and no more than
// this, so a step up reads as a short list of reasons.
const MAX_DIFFERENCES = 3;

const ENTERPRISE_TAGLINE = "For agencies and 150+ interviews a quarter. Talk to sales";

// usage_page_viewed's source for each way into this page (location.state.from,
// or "email"). The limit pop-up counts as a limit banner.
const USAGE_PAGE_SOURCES = { sidebar: "sidebar", banner: "limit_banner", wall: "limit_banner", email: "email_link" };
// Ways in that were about plans, so count as upgrade_page_viewed on arrival
const PLANS_TRIGGERS = ["wall", "banner", "email"];

const SubscriptionUsage = () => {
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [subscriptionUsage, setSubscriptionUsage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUpgrading, setIsUpgrading] = useState(null);
  const [upgradeError, setUpgradeError] = useState(null);
  const [billingCycle, setBillingCycle] = useState("quarterly");
  const [plans, setPlans] = useState([]);
  const [userCountry, setUserCountry] = useState("US");
  // The purchase waiting for "Pay": { kind: "upgrade" | "new" | "topup", planId?, packs? }.
  const [confirm, setConfirm] = useState(null);
  // What the last purchase granted, from /verify-payment, for the done message.
  const [done, setDone] = useState(null);
  const [topupPacks, setTopupPacks] = useState(1);
  const accessToken = sessionStorage.getItem("accessToken");
  const org_id = useSelector((state) => state.userSlice.org_id);
  // Prefilled into Razorpay Checkout: Razorpay sends its payment emails to this
  // address (GenieHire's own payment emails are off by default).
  const payerName = useSelector((state) => state.userSlice.name);
  const payerEmail = useSelector((state) => state.userSlice.email);
  const payerContact = useSelector((state) => state.userSlice.contact);
  const navigate = useNavigate();
  // ?plan=<plan_id>: the plan the limit pop-up recommended. The page opens on
  // that plan's cycle with its card outlined.
  const [searchParams] = useSearchParams();
  const preselectedPlanId = searchParams.get("plan");
  const topupParam = searchParams.get("topup");
  // What this organization may buy right now: per-plan "upgrade" / "available
  // from <date>", the top-up pack, and the unused credits an upgrade carries.
  const { options: purchase, reload: reloadPurchase } = usePurchaseOptions({ enabled: Boolean(org_id) });

  const fetchData = useCallback(async () => {
    try {
        setIsLoading(true);
        setError(null);

        // Only a hint, and only for organizations with no country on record:
        // the backend bills by the country stored on the organization and
        // ignores this when it has one. (This block used to throw the lookup
        // away and pin every customer to "US", which charged Indian
        // organizations in dollars.) The top-up card (usePurchaseOptions)
        // sends the same hint.
        const country = await getCountryHint();
        setUserCountry(country);

        const [usageResponse, plansResponse] = await Promise.all([
            // A 404 means no active subscription or no usage document: the
            // page then shows the plans without the "Your plan" box.
            axios.get(`${host}/get_subscription_usage`, {
                headers: { Authorization: `Bearer ${accessToken}` },
                params: { org_id },
            }).catch((err) => {
                if (err.response?.status === 404) return null;
                throw err;
            }),
            // Sent signed in, so the prices shown are the ones this
            // organization will actually be charged.
            axios.get(`${host}/get_plans`, {
                headers: { Authorization: `Bearer ${accessToken}` },
                params: { country: country || "US" },
            }),
        ]);

        setSubscription(usageResponse?.data.subscription || null);
        setUsage(usageResponse?.data.usage || null);
        setSubscriptionUsage(usageResponse?.data || null);
        setPlans(plansResponse.data.plans);
    } catch (err) {
        setError("Failed to load subscription data");
    } finally {
        setIsLoading(false);
    }
}, [org_id, accessToken]);

  useEffect(() => {
    if (!org_id) return;
    fetchData();
    }, [fetchData]);

  // Where this visit came from: the links into this page say so in their
  // navigation state (from: sidebar | banner | wall), and an email link
  // carries ?src=email or utm_medium=email.
  const location = useLocation();
  const arrivedFrom = useRef(
    location.state?.from
      || (searchParams.get("src") === "email" || searchParams.get("utm_medium") === "email" ? "email" : null)
  ).current;

  // upgrade_page_viewed: the plans on this page were looked at, once per page
  // view. Separates "nobody looks at the plans" from "they look and don't buy".
  const plansViewed = useRef(false);
  const plansSectionRef = useRef(null);
  const markPlansViewed = useCallback((trigger) => {
    if (plansViewed.current) return;
    plansViewed.current = true;
    track(EVENTS.UPGRADE_PAGE_VIEWED, { trigger });
  }, []);

  useEffect(() => {
    track(EVENTS.USAGE_PAGE_VIEWED, { source: USAGE_PAGE_SOURCES[arrivedFrom] || "other" });
    // Sent here to look at plans: counted as a plans view straight away
    if (PLANS_TRIGGERS.includes(arrivedFrom)) markPlansViewed(arrivedFrom);
  }, [arrivedFrom, markPlansViewed]);

  // ...or once the plans themselves come on screen
  useEffect(() => {
    const section = plansSectionRef.current;
    if (!section || plansViewed.current || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          markPlansViewed("scroll");
          observer.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, [plans.length, markPlansViewed]);

  // Land on a cycle that has plans. Without this, a default of "monthly" hides
  // everything when only an annual plan is on sale.
  useEffect(() => {
    const cycles = CYCLE_OPTIONS
      .map((c) => c.value)
      .filter((c) => plans.some((p) => p.is_purchasable && p.billing_cycle === c));
    if (cycles.length && !cycles.includes(billingCycle)) setBillingCycle(cycles[0]);
  }, [plans, billingCycle]);

  // Arriving from the limit pop-up: switch to the recommended plan's cycle once
  // the plans load, then bring its card into view.
  useEffect(() => {
    const target = plans.find((p) => p.plan_id === preselectedPlanId && p.is_purchasable);
    if (!target) return;
    setBillingCycle(target.billing_cycle);
    const timer = setTimeout(() => {
      document.getElementById(`plan-card-${target.plan_id}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
    return () => clearTimeout(timer);
  }, [plans, preselectedPlanId]);

  // ?plan=<id> or ?topup=<packs> from a pop-up's Continue: open that purchase's
  // confirmation once the options have loaded. Only once per visit.
  const openedFromLink = useRef(false);
  useEffect(() => {
    if (openedFromLink.current || !purchase || !plans.length) return;
    const packs = parseInt(topupParam || "", 10);
    if (packs > 0 && purchase.topup?.allowed) {
      openedFromLink.current = true;
      setTopupPacks(Math.min(packs, purchase.topup.max_packs || 10));
      setConfirm({ kind: "topup", packs: Math.min(packs, purchase.topup.max_packs || 10) });
      return;
    }
    const entry = preselectedPlanId && purchase.plans?.[preselectedPlanId];
    if (entry?.allowed) {
      openedFromLink.current = true;
      setConfirm({ kind: entry.kind, planId: preselectedPlanId });
    }
  }, [purchase, plans, preselectedPlanId, topupParam]);

  // Consult an Expert (Enterprise): emails sales this account's details.
  // null -> "sending" -> "sent" | "error"
  const [expertRequest, setExpertRequest] = useState(null);
  const requestCustomPlan = async () => {
    if (expertRequest === "sending" || expertRequest === "sent") return;
    setExpertRequest("sending");
    try {
      await axios.post(`${host}/request-custom-plan`, {}, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setExpertRequest("sent");
    } catch {
      setExpertRequest("error");
    }
  };

  const openPurchase = (planId) => {
    const card = plans.find((p) => p.plan_id === planId);
    markPlansViewed("button");
    track(EVENTS.UPGRADE_CLICKED, {
      plan_id: planId,
      kind: purchase?.plans?.[planId]?.kind,
      amount: card?.amount,
      currency: card?.currency,
      billing_cycle: card?.billing_cycle,
      source: "plans_page",
    });
    if (planId === "plan_enterprise") {
      requestCustomPlan();
      return;
    }
    const entry = purchase?.plans?.[planId];
    if (!entry?.allowed) return;
    setUpgradeError(null);
    setConfirm({ kind: entry.kind, planId });
  };

  // Runs after the customer confirms. Plans and top-ups share the same
  // Razorpay flow; the server decides the amount either way.
  const startCheckout = async ({ kind, planId, packs }) => {
    const loaded = await loadRazorpayScript();
    if (!loaded) {
        setUpgradeError("Failed to load payment gateway. Please try again.");
        return;
    }

    const isTopup = kind === "topup";
    const purchasePlanId = isTopup ? TOPUP_PLAN_ID : planId;
    const plan = isTopup ? null : plans.find((p) => p.plan_id === planId);
    const busyKey = isTopup ? "topup" : planId;
    const label = isTopup
      ? `${(purchase?.topup?.interviews_per_pack || 5) * packs} interviews top-up`
      : `${plan.display_name} plan`;

    try {
      setIsUpgrading(busyKey);
      setUpgradeError(null);
      track(EVENTS.UPGRADE_CONFIRMED, {
        plan_id: purchasePlanId,
        kind,
        packs: isTopup ? packs : undefined,
        amount: plan?.amount,
        currency: plan?.currency,
        billing_cycle: plan?.billing_cycle,
      });

      const orderResponse = await axios.post(
        `${host}/create-payment-order`,
        {
          plan_id: purchasePlanId,
          org_id,
          billing_cycle: plan?.billing_cycle || billingCycle,
          country: userCountry,
          packs: isTopup ? packs : undefined,
        },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      const { order_id, amount, currency, key_id } = orderResponse.data;
      const planIdForEvents = purchasePlanId;

      const options = {
        key:         key_id,
        amount,
        currency,
        order_id,
        name: "GenieHire",
        description: label,
        image:       "https://nsqrbucket.s3.ap-south-1.amazonaws.com/images/genieHire_email_logo.png",
        prefill: {
          name: payerName || undefined,
          email: payerEmail || undefined,
          contact: payerContact || undefined,
        },
        notes: {
          purchase: label,
        },
        method: {
            card:        true,
            upi:         true,
            netbanking:  true,
            wallet:      false,
            paylater:    false,
            emi:         false,
        },
        handler: async (response) => {
          try {
            const verified = await axios.post(
              `${host}/verify-payment`,
              {
                razorpay_order_id:   response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature:  response.razorpay_signature,
                plan_id:             purchasePlanId,
                org_id,
              },
              { headers: { Authorization: `Bearer ${accessToken}` } }
            );
            // upgrade_completed is sent by the server when the purchase is applied
            setDone(verified.data);
            await Promise.all([fetchData(), reloadPurchase()]);
          } catch (err) {
            track(EVENTS.UPGRADE_PAYMENT_FAILED, {
              plan_id: planIdForEvents,
              reason: "verify_failed",
            });
            setUpgradeError(
              "Your payment went through, but we couldn't add it to your account yet. "
              + "You don't need to pay again; our team has been alerted and will fix it shortly."
            );
          } finally {
            setIsUpgrading(null);
          }
        },
        theme: { color: "#2F64FF" },
        modal: {
          ondismiss: () => { setIsUpgrading(null); },
        },
      };

      const rzp = new window.Razorpay(options);

      // Without this a declined payment only ever appears inside Razorpay's own
      // dialog: this page would keep showing "Opening checkout..." and never say
      // what went wrong. Nothing was charged, so the plan is untouched.
      rzp.on("payment.failed", (event) => {
        const reason = event?.error?.description || "The payment could not be completed.";
        const step = event?.error?.reason;
        track(EVENTS.UPGRADE_PAYMENT_FAILED, {
          plan_id: planIdForEvents,
          reason: step || "unknown",
          code: event?.error?.code || "",
        });
        // So the customer also gets the "didn't go through" email. The server
        // sends it once per order however many attempts fail, and never for an
        // order that ends up paid.
        axios.post(
          `${host}/payment-failed`,
          { razorpay_order_id: order_id, reason, code: event?.error?.code || "" },
          { headers: { Authorization: `Bearer ${accessToken}` } }
        ).catch(() => {});
        setUpgradeError(
          // "invalid_token" is a saved card whose token no longer works --
          // paying with the full card details instead gets past it.
          step === "invalid_token" || /invalid token/i.test(reason)
            ? "That saved card could not be used. Please enter your card details again, or pay by UPI."
            : reason
        );
        setIsUpgrading(null);
      });

      track(EVENTS.UPGRADE_CHECKOUT_OPENED, { plan_id: planIdForEvents });
      // Razorpay's window takes over from here; the confirmation has done its job.
      setConfirm(null);
      rzp.open();

    } catch (err) {
      // The server explains a refusal ("You can move to this plan when your
      // current plan ends on ..."); anything else is a plain retry.
      const detail = err?.response?.data?.detail;
      setUpgradeError(typeof detail === "string" ? detail : "Failed to initiate payment. Please try again.");
      setConfirm(null);
      setIsUpgrading(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-[#2F64FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <p className="text-sm dark:text-gray-400 text-textSecondary py-6 text-center">
        {error}
      </p>
    );
  }

  // No active subscription or usage document: only the plan cards are shown.
  const hasPlan = Boolean(subscription && usage);

  // plan_id_str is the plan slug resolved by /get_subscription_usage. Every trial
  // org is on plan_trial ("Trial2"); plan_trial_version is a retired lookalike.
  // A plan that has ended is not "current": its card must offer Upgrade again,
  // or an expired customer could never renew the plan they were on.
  const subscriptionEnded = hasPlan && subscription.end_date
    ? new Date(subscription.end_date).getTime() <= Date.now()
    : false;
  const currentPlanId = !hasPlan || subscriptionEnded ? null : subscription.plan_id_str || "plan_trial";

  // Only offer cycles we actually sell, so the toggle can never switch the page to
  // a cycle with nothing in it.
  const availableCycles = CYCLE_OPTIONS
    .map((c) => c.value)
    .filter((c) => plans.some((p) => p.is_purchasable && p.billing_cycle === c));

  // Purchasable plans belong to one billing cycle; Enterprise is shown whatever
  // cycle is selected. A plan that cannot be bought -- the trial, or a document
  // not yet migrated -- is left out: the "Your plan" bar above already says
  // what the organization is on, and a column with no button only crowds the
  // comparison.
  const visiblePlans = plans.filter((plan) =>
    plan.is_purchasable ? plan.billing_cycle === billingCycle : plan.is_enterprise
  );

  // Missing until the API that returns countable_features is deployed; the
  // cards then fall back to listing every highlight.
  const hasCounts = visiblePlans.some(
    (p) => p.countable_features && Object.keys(p.countable_features).length > 0
  );

  // On/off features, judged across the plans that can be bought. One that all
  // of them have goes in "Every plan includes"; one only some have is listed
  // on those plans' cards. hr_creation_enabled is left out: the Team members
  // count already says it. Empty until boolean_features is deployed.
  const paidPlans = visiblePlans.filter((p) => p.is_purchasable && p.boolean_features);
  const listedBools = BOOL_DEFS.filter((d) => d.key !== "hr_creation_enabled");
  const sharedFeatures = paidPlans.length
    ? listedBools.filter((d) => paidPlans.every((p) => p.boolean_features[d.key] === true))
    : [];
  const planOnlyFeatures = (plan) =>
    plan.is_purchasable && plan.boolean_features
      ? listedBools
          .filter((d) => plan.boolean_features[d.key] === true && !sharedFeatures.includes(d))
          .map((d) => d.title)
      : [];

  // What a longer cycle saves, as months of the shorter cycle's price: annual
  // at Rs 49,999 against four quarters at Rs 14,999 is two months free. Shown on
  // the toggle only when it is at least one month.
  const monthsFree = {};
  CYCLE_OPTIONS.forEach(({ value }) => {
    let best = 0;
    plans
      .filter((p) => p.is_purchasable && p.billing_cycle === value)
      .forEach((p) => {
        plans
          .filter((q) => q.is_purchasable && q.plan_name === p.plan_name && q.currency === p.currency
            && (CYCLE_MONTHS[q.billing_cycle] || 1) < (CYCLE_MONTHS[value] || 1))
          .forEach((q) => {
            const perMonthShort = q.amount / (CYCLE_MONTHS[q.billing_cycle] || 1);
            if (perMonthShort <= 0) return;
            const full = perMonthShort * (CYCLE_MONTHS[value] || 1);
            best = Math.max(best, Math.round((full - p.amount) / perMonthShort));
          });
      });
    monthsFree[value] = best;
  });

  // Price per interview is the line that makes each step up read as a saving
  // (Rs 300 on Essential against Rs 200 on Corporate).
  const interviewsOf = (p) => Number(p.countable_features?.interview_count_v2) || 0;
  const perInterview = (p) =>
    p.is_purchasable && p.amount > 0 && interviewsOf(p) > 0
      ? Math.round(p.amount / interviewsOf(p))
      : null;
  const priced = visiblePlans.filter((p) => perInterview(p) !== null);
  const cheapestId = priced.length > 1
    ? priced.reduce((a, b) => (perInterview(b) < perInterview(a) ? b : a)).plan_id
    : null;

  const tagline = (plan) => {
    if (plan.is_enterprise) return ENTERPRISE_TAGLINE;
    if (plan.plan_id === cheapestId) return "Lowest price per interview";
    if (plan.is_most_popular) return "Most teams pick this";
    return "";
  };

  // Highlights and plan-only features, minus the headline numbers.
  const cardItems = (plan) => [
    ...(plan.highlights || []).filter((t) => !RESTATES_HEADLINE.test(t)),
    ...planOnlyFeatures(plan),
  ];
  // What this plan adds over the one to its left; the first card has nothing
  // to its left, so it lists its own.
  const differences = (plan, index) => {
    const left = index > 0 ? visiblePlans[index - 1] : null;
    const leftItems = new Set(left ? cardItems(left) : []);
    return cardItems(plan).filter((t) => !leftItems.has(t)).slice(0, MAX_DIFFERENCES);
  };

  const renderCta = (plan) => {
    const isCurrent = plan.plan_id === currentPlanId;
    if (isCurrent) {
      return (
        <button
          disabled
          className="w-full py-2 rounded-full text-[13px] font-semibold bg-[#F4F4F6] dark:bg-gray-800 text-[#9CA3AF] cursor-not-allowed"
        >
          Your current plan
        </button>
      );
    }
    if (plan.is_enterprise) {
      return (
        <>
          <button
            onClick={() => openPurchase(plan.plan_id)}
            disabled={expertRequest === "sending" || expertRequest === "sent"}
            className="w-full py-2 rounded-full text-[13px] font-semibold bg-[#404040] dark:bg-white text-white dark:text-[#20202B] hover:opacity-90 transition-opacity disabled:opacity-70 disabled:cursor-default"
          >
            {expertRequest === "sending"
              ? "Sending..."
              : expertRequest === "sent"
              ? "Request sent"
              : "Consult an Expert"}
          </button>
          {expertRequest === "sent" && (
            <p className="text-[12px] text-center text-[#1F7A43] dark:text-green-300 mt-2">
              Our team will get in touch with you shortly.
            </p>
          )}
          {expertRequest === "error" && (
            <p className="text-[12px] text-center text-red-500 mt-2">
              Couldn't send your request. Please try again, or write to sales@geniehire.ai.
            </p>
          )}
        </>
      );
    }
    if (!plan.is_purchasable) return null;

    // Mid-term only a plan that costs more can be bought; the rest open up
    // when the current plan ends.
    const entry = purchase?.plans?.[plan.plan_id];
    if (entry && !entry.allowed) {
      return (
        <button
          disabled
          className="w-full py-2 rounded-full text-[13px] font-semibold bg-[#F4F4F6] dark:bg-gray-800 text-[#9CA3AF] cursor-not-allowed"
        >
          {entry.reason === "sales"
            ? "Contact sales to change plan"
            : `Available from ${formatDate(entry.available_from, { year: false })}`}
        </button>
      );
    }
    return (
      <>
        <button
          onClick={() => openPurchase(plan.plan_id)}
          disabled={isUpgrading === plan.plan_id || !entry}
          // Gradient border and gradient text, transparent inside (gradient-frame
          // in App.css), so it sits on the card in both themes.
          className={`gradient-frame w-full py-2 rounded-full text-[13px] font-semibold transition-colors hover:bg-[#2F64FF]/[0.06] ${
            isUpgrading === plan.plan_id || !entry ? "opacity-60 cursor-not-allowed" : ""
          }`}
        >
          <span className="bg-gradient-to-r from-[#2F64FF] to-[#FB54AE] bg-clip-text text-transparent">
            {isUpgrading === plan.plan_id
              ? "Opening checkout..."
              : entry?.kind === "new" && (subscriptionEnded || !hasPlan)
              ? `Buy ${plan.display_name}`
              : "Upgrade"}
          </span>
        </button>
      </>
    );
  };

  const topup = purchase?.topup;
  const doneMessage = (() => {
    if (!done) return null;
    // No details: the purchase was still being added when the reply came back.
    // The page has reloaded the account, so the plan box above is up to date.
    if (!done.kind) {
      return {
        title: "Payment received",
        body: "Your purchase has been added to your account. Your plan details above are updated.",
      };
    }
    const b = done.balance || {};
    const until = formatDate(done.end_date);
    if (done.kind === "topup") {
      return {
        title: `${formatCount(done.added?.interviews)} interviews and ${formatCount(done.added?.cv_screenings)} CV screenings added`,
        body: `You have ${formatCount(b.interviews)} interviews and ${formatCount(b.cv_screenings)} CV screenings, usable until ${until}.`,
      };
    }
    return {
      title: `You're on ${done.plan_display_name || done.plan} until ${until}`,
      body: `${formatCount(b.interviews)} interviews and ${formatCount(b.cv_screenings)} CV screenings are ready to use.`
        + (done.reports_unlocked ? ` ${done.reports_unlocked} locked report${done.reports_unlocked === 1 ? " is" : "s are"} now open.` : ""),
    };
  })();

  const formatPrice = (plan, amount) => {
    const symbol = plan.currency === "INR" ? "₹" : "$";
    const locale = plan.currency === "INR" ? "en-IN" : "en-US";
    return `${symbol}${Number(amount || 0).toLocaleString(locale)}`;
  };

  const monthlyPriceLabel = (plan) =>
    formatPrice(plan, Math.ceil((plan.amount || 0) / (CYCLE_MONTHS[plan.billing_cycle] || 1)));

  return (
    <div className="min-h-screen bg-bgPrimary">
      <div className="mx-auto px-6 py-8">

        {/* ── Header ── */}
        <div className="mb-2">
          <div className="min-w-0">
            <div className="flex items-center gap-3 mb-1">
              <button
                onClick={() => navigate(-1)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-200 transition-colors"
              >
                <ArrowLeft className="text-gray-500 w-4 h-4" />
              </button>
              <h2 className="text-[#404040] dark:text-[#AEB9E1] text-lg font-semibold tracking-wide">Plans &amp; Payments</h2>
            </div>
            <p className="text-sm text-[#9CA3AF] dark:text-gray-400 ml-11 max-w-xl">
              Upgrade to access GenieHire.ai's premium capabilities. Compare plans and choose the one that fits your hiring goals.
            </p>
          </div>
        </div>

        <div className="mt-6">
          <>
            {/* ── What the plan grants, and what is left of it ── */}
            {/* The same component the dashboard used to carry, so the usage a
                recruiter sees here is the usage they saw there. */}
            {/* ml-11 lines the card up with the page title, past the back arrow. */}
            {hasPlan ? (
              <div className={`${topup?.allowed ? "mb-4" : "mb-8"} md:ml-11`}>
                <SubscriptionUsageBox data={subscriptionUsage} title="Your plan" />
              </div>
            ) : (
              <p className="mb-8 md:ml-11 text-[14px] font-semibold text-[#404040] dark:text-[#AEB9E1]">
                No active plan found
              </p>
            )}

            {doneMessage && (
              <div className="mb-6 md:ml-11 rounded-xl border border-[#BFE6CB] dark:border-green-900/60 bg-[#EEF9F1] dark:bg-green-900/20 px-5 py-4 flex items-start justify-between gap-4">
                <div>
                  <p className="text-[14px] font-semibold text-[#1F7A43] dark:text-green-300">{doneMessage.title}</p>
                  <p className="text-[13px] text-[#2F6B45] dark:text-green-200/80 mt-0.5">{doneMessage.body}</p>
                </div>
                <button onClick={() => setDone(null)} className="text-[12px] text-[#2F6B45] dark:text-green-200/80 hover:underline shrink-0">
                  Dismiss
                </button>
              </div>
            )}

            {upgradeError && (
              <p className="text-[13px] text-red-500 mb-4 md:ml-11">{upgradeError}</p>
            )}

            {/* ── Top-ups: more interviews on the current plan, same end date ── */}
            {hasPlan && topup?.allowed && (
              <div id="topup-card" className="mb-8 md:ml-11 rounded-xl border border-[#EBEAED] dark:border-gray-700/80">
                <div className="rounded-xl bg-bgSecondary px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[14px] font-semibold text-[#404040] dark:text-[#AEB9E1]">Need more interviews?</p>
                    <p className="text-[12.5px] text-[#6B7280] dark:text-gray-400 mt-0.5">
                      {formatMoney(topup.pack_price, topup.currency)} for {topup.interviews_per_pack} interviews + {formatCount(topup.cvs_per_pack)} CV screenings
                      {" "}· usable until {formatDate(topup.valid_until)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="flex items-center gap-2" role="group" aria-label="Number of packs">
                      <button
                        onClick={() => setTopupPacks((n) => Math.max(1, n - 1))}
                        disabled={topupPacks <= 1}
                        aria-label="One pack fewer"
                        className="w-8 h-8 rounded-full border border-[#EBEAED] dark:border-gray-700 text-[16px] leading-none text-[#404040] dark:text-gray-200 hover:border-[#2F64FF]/60 disabled:opacity-40"
                      >−</button>
                      <span className="min-w-[124px] text-center text-[13px] text-[#404040] dark:text-gray-200" aria-live="polite">
                        <b>{topupPacks} pack{topupPacks === 1 ? "" : "s"}</b> · {topupPacks * topup.interviews_per_pack} interviews
                      </span>
                      <button
                        onClick={() => setTopupPacks((n) => Math.min(topup.max_packs || 10, n + 1))}
                        disabled={topupPacks >= (topup.max_packs || 10)}
                        aria-label="One pack more"
                        className="w-8 h-8 rounded-full border border-[#EBEAED] dark:border-gray-700 text-[16px] leading-none text-[#404040] dark:text-gray-200 hover:border-[#2F64FF]/60 disabled:opacity-40"
                      >+</button>
                    </div>
                    <button
                      onClick={() => {
                        markPlansViewed("button");
                        track(EVENTS.UPGRADE_CLICKED, {
                          plan_id: TOPUP_PLAN_ID,
                          kind: "topup",
                          packs: topupPacks,
                          source: "plans_page",
                        });
                        setUpgradeError(null);
                        setConfirm({ kind: "topup", packs: topupPacks });
                      }}
                      disabled={isUpgrading === "topup"}
                      className="h-9 px-5 rounded-full text-[13px] font-semibold text-white bg-gradient-to-r from-[#2F64FF] to-[#FB54AE] hover:opacity-90 disabled:opacity-60 whitespace-nowrap"
                    >
                      Buy for {formatMoney(topup.pack_price * topupPacks, topup.currency)}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <PurchaseConfirmModal
              open={Boolean(confirm)}
              onClose={() => setConfirm(null)}
              busy={Boolean(isUpgrading)}
              kind={confirm?.kind}
              plan={confirm?.planId ? plans.find((p) => p.plan_id === confirm.planId) : null}
              packs={confirm?.packs || 1}
              options={purchase}
              onConfirm={() => startCheckout(confirm)}
            />

            {/* ── Plans ── */}
            {/* The usual SaaS pricing layout: a cycle toggle that states the
                saving, one card per tier with its price and button first, the
                limits that differ between tiers next, and what every tier
                shares said once underneath instead of ticked on every card.
                Themed with the platform's gradient (#2F64FF -> #FB54AE) and
                card surface (bgSecondary) so it reads as part of the product. */}
            {/* Same left edge and full width as the "Your plan" card above. */}
            <div ref={plansSectionRef} className="pb-12 md:ml-11">
              {availableCycles.length > 1 && (
                <div className="flex justify-center mb-5">
                  <div className="inline-flex items-center gap-1 bg-bgSecondary border border-[#EBEAED] dark:border-gray-700/80 rounded-full p-1">
                    {CYCLE_OPTIONS
                      .filter(({ value }) => availableCycles.includes(value))
                      .map(({ value, label }) => (
                        <button
                          key={value}
                          onClick={() => setBillingCycle(value)}
                          className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[13px] font-medium transition-all ${
                            billingCycle === value
                              ? "bg-gradient-to-r from-[#2F64FF] to-[#FB54AE] text-white shadow-sm"
                              : "text-[#9CA3AF] hover:text-[#404040] dark:hover:text-gray-200"
                          }`}
                        >
                          {label}
                          {monthsFree[value] > 0 && (
                            <span
                              className={`px-1.5 py-px rounded-full text-[9px] font-bold ${
                                billingCycle === value
                                  ? "bg-white/25 text-white"
                                  : "bg-[#EAF7EC] text-[#3E8C48] dark:bg-green-900/30 dark:text-green-300"
                              }`}
                            >
                              {monthsFree[value]} month{monthsFree[value] === 1 ? "" : "s"} free
                            </span>
                          )}
                        </button>
                      ))}
                  </div>
                </div>
              )}

              {visiblePlans.length === 0 ? (
                <p className="text-sm text-[#9CA3AF] text-center py-10">
                  No plans available right now.
                </p>
              ) : (
                <div
                  className={`grid gap-6 lg:gap-12 items-stretch ${
                    visiblePlans.length === 1
                      ? "max-w-sm"
                      : visiblePlans.length === 2
                      ? "md:grid-cols-2"
                      : visiblePlans.length === 3
                      ? "md:grid-cols-3"
                      : "md:grid-cols-2 xl:grid-cols-4"
                  }`}
                >
                  {visiblePlans.map((plan, index) => {
                    const isCurrent = plan.plan_id === currentPlanId;
                    const popular = plan.is_most_popular && !isCurrent;
                    // Until the API returns counts there is no headline to
                    // compare against, so every highlight is listed.
                    const extras = hasCounts ? differences(plan, index) : plan.highlights || [];
                    const line = tagline(plan);
                    const interviews = interviewsOf(plan);
                    const cvs = Number(plan.countable_features?.resume_screening_count) || 0;
                    const unitPrice = perInterview(plan);

                    const card = (
                      <div className="flex flex-col h-full rounded-[11px] bg-bgSecondary p-6">
                        <div className="flex items-center justify-between gap-2 min-h-[22px]">
                          <p className="text-[15px] font-semibold tracking-wide bg-gradient-to-r from-[#2F64FF] to-[#FB54AE] bg-clip-text text-transparent w-fit">
                            {plan.display_name}
                          </p>
                          {isCurrent && (
                            <span className="whitespace-nowrap px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-white bg-[#404040]">
                              Current Plan
                            </span>
                          )}
                        </div>
                        <p className="text-[12px] text-[#6B7280] dark:text-gray-400 mt-1 min-h-[18px]">
                          {line}
                        </p>

                        <div className="mt-3 flex items-baseline gap-1.5">
                          <span className="text-[32px] leading-none font-bold text-[#404040] dark:text-white tracking-tight">
                            {plan.is_enterprise ? "Custom" : monthlyPriceLabel(plan)}
                          </span>
                          {!plan.is_enterprise && (
                            <span className="text-sm text-[#9CA3AF] font-medium">/ month</span>
                          )}
                        </div>
                        <p className="text-xs text-[#9CA3AF] mt-1.5 min-h-[16px]">
                          {plan.is_enterprise
                            ? "Contact us for pricing"
                            : (CYCLE_MONTHS[plan.billing_cycle] || 1) > 1
                            ? `Billed ${formatPrice(plan, plan.amount)} ${CYCLE_ADVERB[plan.billing_cycle]}`
                            : ""}
                        </p>

                        <div className="mt-5">{renderCta(plan)}</div>

                        {/* Interviews are the headline; the price per
                            interview sits right under them. */}
                        {hasCounts && (
                          <div className="mt-6 pt-5 border-t border-[#EBEAED] dark:border-gray-700/60">
                            <div className="flex items-baseline gap-2">
                              <span className="text-[26px] leading-none font-bold text-[#404040] dark:text-white tracking-tight">
                                {plan.is_enterprise ? "Custom" : interviews.toLocaleString("en-IN")}
                              </span>
                              <span className="text-[13px] text-[#6B7280] dark:text-gray-400">
                                {plan.is_enterprise
                                  ? "AI interview volume"
                                  : `AI interviews a ${CYCLE_NOUN[plan.billing_cycle] || "cycle"}`}
                              </span>
                            </div>
                            <p className="mt-1.5 text-[13px] font-semibold text-[#2F64FF] min-h-[20px]">
                              {unitPrice !== null ? `${formatPrice(plan, unitPrice)} per interview` : ""}
                            </p>
                            <p className="mt-2 text-[13px] text-[#4B5563] dark:text-gray-300">
                              {plan.is_enterprise
                                ? "Custom CV screening volume"
                                : cvs > 0
                                ? `${cvs.toLocaleString("en-IN")} CV screenings`
                                : ""}
                            </p>
                          </div>
                        )}

                        {extras.length > 0 && (
                          <ul className="mt-5 pt-5 border-t border-[#EBEAED] dark:border-gray-700/60 space-y-2.5">
                            {extras.map((item, i) => (
                              <li
                                key={i}
                                className="flex items-start gap-2 text-[13px] text-[#4B5563] dark:text-gray-300"
                              >
                                <Check className="w-4 h-4 flex-shrink-0 mt-0.5 text-[#2F64FF]" strokeWidth={2.5} />
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );

                    // The recommended tier gets a gradient frame; the others a
                    // hairline, so the eye lands on one card first. The plan
                    // the limit pop-up sent them for gets an extra outline.
                    const preselected = plan.plan_id === preselectedPlanId
                      ? " ring-2 ring-[#2F64FF] ring-offset-4 ring-offset-bgPrimary"
                      : "";
                    return popular ? (
                      <div
                        key={plan.plan_id}
                        id={`plan-card-${plan.plan_id}`}
                        className={`rounded-xl gradient-frame shadow-lg${preselected}`}
                      >
                        {card}
                      </div>
                    ) : (
                      <div
                        key={plan.plan_id}
                        id={`plan-card-${plan.plan_id}`}
                        className={`rounded-xl border ${
                          isCurrent
                            ? "border-[#404040] dark:border-[#AEB9E1]/50"
                            : "border-[#EBEAED] dark:border-gray-700/80"
                        }${preselected}`}
                      >
                        {card}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Said once, not ticked on every card: every self-serve plan
                  carries the same on/off features and differs only in counts. */}
              {sharedFeatures.length > 0 && (
                <div className="mt-6 rounded-xl bg-bgSecondary px-6 py-5">
                  <p className="text-[13px] font-semibold text-[#404040] dark:text-[#AEB9E1] mb-3">
                    Every plan includes
                  </p>
                  <div className="flex flex-wrap gap-x-6 gap-y-2.5">
                    {sharedFeatures.map((def) => {
                      const Icon = def.icon;
                      return (
                        <span
                          key={def.key}
                          className="inline-flex items-center gap-2 text-[13px] text-[#4B5563] dark:text-gray-300"
                        >
                          <Icon className="w-4 h-4 text-[#A78BFA]" strokeWidth={1.75} />
                          {def.title}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </>
        </div>

      </div>
    </div>
  );
};

export default SubscriptionUsage;